/* eslint no-case-declarations: 0 */

import React, { useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import { useDrop } from 'react-dnd';
import { isSplitChequeCrashFixCompany, runDndDrop } from '~splitChequeCrashFix';
import SelectOptionModal from '~Components/SelectOptionModal';
import itemsType from '../dragItemsType';
import { range } from 'lodash';
import { useI18n, useSelector, useStaffCan } from '~Hooks';
import _ from 'lodash';
import { useOrderStatusApi, useOrderStatusUniverse } from '~Components/OrderStatusUniverse';
import { BookingStatus, ORDER_STATUS } from '~../__queries__';
import { useStorage } from '~Components/Storage';
import { useBookings } from '~Providers/BookingsProvider';

const dropAnimationDuration = 500;

const Wrapper = styled.div<{
  active: boolean;
  selected: boolean;
  canDrop: boolean;
  isOver: boolean;
  showDropAnimation: boolean;
  hasPrinted: boolean;
  grid: boolean;
  staffCanMutate: boolean;
  shape?: number; // Table shapes: 0 = square, 1 = circle, 2 = long laying table, 3 = long standing table, 4 = large circle
  activity?: 'moderate' | 'stale' | 'none';
  hasBooking?: boolean;
}>`
  background: ${(props) => {
    if (props.active || props.isOver) {
      switch (props.activity) {
        case 'moderate':
          return 'rgba(255, 237, 204, 0.864)';
        case 'stale':
          return 'rgba(255, 166, 166, 0.822)';
        default:
          return '#d5d7d8';
      }
    } else {
      return '#545960';
    }
  }};
  border-radius: ${(props) => (props.shape === 1 || props.shape === 4 ? '50%' : '4px')};
  height: ${({ shape }) => {
    switch (shape) {
      case 3:
        return '117px';
      case 4:
        return '76px';
      default:
        return '57px';
    }
  }};
  width: ${({ shape }) => {
    switch (shape) {
      case 2:
        return '117px';
      case 4:
        return '76px';
      default:
        return '57px';
    }
  }};
  margin: ${(props) => (props.grid ? '32px' : '0')};
  color: ${(props) => {
    if (props.active) {
      switch (props.activity) {
        case 'moderate':
          return '#220f22';
        case 'stale':
          return '#061026';
        default:
          return '#127843';
      }
    } else {
      return '#1c232c';
    }
  }};
  border: ${(props) =>
    props.selected
      ? '1px solid #ffc000'
      : props.hasBooking
      ? '2px solid #6f4dd8'
      : '1px solid transparent'};
  font-size: 24px;
  font-weight: 600;
  line-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  box-sizing: border-box;
  position: relative;
  z-index: ${(props) => (props.canDrop ? 3 : 'auto')};
  opacity: ${({ staffCanMutate }) => (staffCanMutate ? 1 : 0.6)};
  transition: background-color 0.2s ease-in-out;
  animation: ${(props) =>
    props.showDropAnimation
      ? `
          drop-check ${dropAnimationDuration}ms linear;
        `
      : ''};
  ${(props) => props.selected && 'background: #0e1216; color: #fff'};

  ${({ isOver, staffCanMutate }) =>
    isOver &&
    staffCanMutate &&
    `
    box-shadow: 0px 0px 15px 15px rgba(255,255,255,0.6);
  `}

  ${({ hasPrinted, isOver }) =>
    hasPrinted &&
    !isOver &&
    `
  border: 6px solid rgba(150, 100, 100, 0.8);
`}
`;

const UrgencyDot = styled.span<{ color: string }>`
  height: 8px;
  width: 8px;
  background-color: ${({ color }) => color};
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 50%;
`;

const PeopleCount = styled.span`
  height: 24px;
  width: 24px;
  background-color: #c3c5c7;
  position: absolute;
  top: -8px;
  right: -8px;
  border-radius: 50%;
  text-align: center;
  font-size: 14px;
  color: #1c232c;
  padding-right: 2px;
  line-height: 23px;
  box-sizing: border-box;
`;

const Note = styled.span`
  position: absolute;
  bottom: -28px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 4px;
  max-width: 80px;
  height: 24px;
  background-color: #c3c5c7;
  font-size: 12px;
  color: #0e1216;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 2px 8px;
`;

const CourseStage = styled.span<{
  color: string;
}>`
  height: 24px;
  width: 24px;
  background-color: ${(props) => props.color};
  position: absolute;
  top: -10px;
  left: -10px;
  border-radius: 50%;
  text-align: center;
  font-size: 12px;
  color: #1c232c;
  padding-right: 2px;
  line-height: 23px;
  box-sizing: border-box;
`;

// Inner flex column so table name and seating time stack without overlapping
const TableNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1;
`;

const SeatingTime = styled.span`
  font-size: 10px;
  font-weight: 400;
  line-height: 10px;
  color: inherit;
  opacity: 0.8;
  white-space: nowrap;
`;

const OrdersCount = styled.span<{
  color: string;
}>`
  height: 19px;
  line-height: 18px;
  width: 47px;
  background-color: ${(props) => props.color};
  position: absolute;
  bottom: -10px;
  left: 5px;
  border-radius: 4px;
  text-align: center;
  font-size: 6px;
  color: #fff;
  padding-right: 2px;
  border-top: 1px solid rgba(255, 255, 255, 0.4);
  box-sizing: border-box;

  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.2);
`;

export const statusBackgroundColor = (status: ORDER_STATUS) => {
  switch (status) {
    case ORDER_STATUS.ACCEPTED:
      return '#1642be';
    case ORDER_STATUS.FINALIZING:
      return '#ff9100';
    case ORDER_STATUS.READY:
      return '#209946';
    default:
      return '#515A5A';
  }
};

interface Props {
  id: string;
  tableName: string;
  tableShape?: number;
  peopleCount?: number;
  activityStatus: 'moderate' | 'stale' | 'none';
  active: boolean;
  selected: boolean;
  setSelectedTable: (id: string, name: string, coverage?: number) => void;
  onDoubleClick?: (id: string, name: string, coverage?: number) => void;
  courseStageId: string | null;
  grid?: boolean;
  hasPrinted: boolean;
  /** Elapsed seating time string, e.g. "1h 23m" or "45m" */
  seatingTime?: string;
}

const TableItem: React.FunctionComponent<Props> = ({
  id,
  active,
  selected,
  tableName,
  tableShape,
  peopleCount,
  activityStatus,
  setSelectedTable,
  onDoubleClick,
  hasPrinted,
  courseStageId,
  grid,
  seatingTime,
}) => {
  const { i18n } = useI18n();
  const [storage] = useStorage();
  const [showDropAnimation, setShowDropAnimation] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tableCheque, setTableCheque] = useState<string | null>(null);
  const bookings = useBookings();
  const venueId = useSelector((state) => state.venueId!);
  const orderStatusApi = useOrderStatusApi(venueId);

  const courseTypes = useSelector((state) => state.courseTypes);

  const tableHasBookings = bookings.today.some(
    (booking) =>
      booking.tableIds?.includes(id) &&
      (booking.status === BookingStatus.ACCEPTED || booking.status === BookingStatus.STARTED),
  );

  const orderStatusUniverse = useOrderStatusUniverse();

  const orders = orderStatusUniverse.orders.filter(
    (o) => o.tableId === id || o.tableId === tableName,
  );
  const order = orders[0];

  const [{ KDSShowOrderStatusInTSR }] = useStorage();

  const api = useTableApi();
  const staffCan = useStaffCan();
  // only defer the drop mutation for the affected company
  const useFix = isSplitChequeCrashFixCompany(useSelector((state) => state.company));
  const tableUniverse = useTableUniverse();

  const noteText = _.get(tableUniverse, `notes[${id}].text`, '');

  // If no staffcodes all tables are owned, else all tables without cheques or with cheques owned by current staff
  const staffCanMutate = staffCan.mutateTable(id);

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [itemsType.CHEQUE, itemsType.ITEM],
    canDrop: () => staffCanMutate,
    drop: (data) => {
      // Defer the mutation until the dnd drop transaction has completed.
      // Mutating synchronously re-renders and unregisters drop targets
      // mid-transaction, crashing react-dnd with "Expected to find a valid target."
      runDndDrop(useFix, () => {
        switch (data.type) {
          case itemsType.CHEQUE:
            // @ts-ignore
            api.moveCheque(data.chequeId, id);
            // @ts-ignore
            api.log.moveCheque(data.chequeId, data.table, { id, name: tableName });
            setSelectedTable(id, tableName, peopleCount);
            enableDropAnimation();

            const ordersOnPreviousTable = orderStatusUniverse.orders.filter(
              // @ts-ignore
              (o) => o.tableId === data.table.id || o.tableId === data.table.name,
            );

            // Find any order that's on the table and update its tableId to the new table
            for (const o of ordersOnPreviousTable) {
              orderStatusApi.moveOrderToTable(o.id, {
                tableId: id,
                tableName: tableName,
              });
            }
            break;

          case itemsType.ITEM:
            // @ts-ignore
            const newChequeId: string = api.moveItemsToTable(data.selectedItems, id);
            // @ts-ignore
            api.log.moveItemsToTable(data.selectedItems, newChequeId, id);

            if (!active) {
              setTableCheque(newChequeId);
              setShowModal(true);
            }
            break;

          default:
            break;
        }
      });
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const enableDropAnimation = () => {
    setShowDropAnimation(true);
    clearDropAnimation();
  };

  const clearDropAnimation = () => {
    setTimeout(() => {
      setShowDropAnimation(false);
    }, dropAnimationDuration);
  };

  // Double-tap detection
  const lastTapRef = useRef<number>(0);
  const doubleTapDelay = 300;

  const handleClick = (e: React.TouchEvent<HTMLDivElement>, id: string, tableName: string) => {
    e.stopPropagation();

    const now = Date.now();
    if (onDoubleClick && now - lastTapRef.current < doubleTapDelay) {
      // Double-tap detected
      lastTapRef.current = 0;
      onDoubleClick(id, tableName, peopleCount);
    } else {
      lastTapRef.current = now;
      setSelectedTable(id, tableName, peopleCount);
    }
  };

  const handleSelectCoverage = (count: number) => {
    if (tableCheque) {
      api.setChequeCoverage(tableCheque, count);
      api.log.setChequeCoverage(tableCheque, count);
    }
    setShowModal(false);
  };

  const courseStage = useMemo(() => {
    if (!courseStageId) {
      return null;
    }

    return courseTypes.find((c) => c.label === courseStageId);
  }, [courseStageId, courseTypes]);

  // Calculate urgency color from endsAt on cheque bookingData
  const urgencyColor = useMemo(() => {
    const tableCheques = tableUniverse.cheques.filter((c) => c.tableId === id);
    const chequeWithEndsAt = tableCheques.find((c) => c.bookingData?.endsAt);
    if (!chequeWithEndsAt?.bookingData?.endsAt) return null;

    const now = new Date();
    const end = new Date(chequeWithEndsAt.bookingData.endsAt);
    const minutesLeft = (end.getTime() - now.getTime()) / (60 * 1000);

    if (minutesLeft <= 30) return '#e3273b';
    if (minutesLeft <= 60) return '#d4775a';
    return null;
  }, [tableUniverse.cheques, id]);

  const isActive = isOver && canDrop;

  // hide if there are 0 people and hideCoverageModal is enabled in the settings
  const showPeopleCount =
    (active && peopleCount !== 0 && storage.hideCoverageModal) ||
    (active && !storage.hideCoverageModal);

  return (
    <>
      <Wrapper
        ref={drop}
        canDrop={canDrop}
        isOver={isOver}
        active={active}
        selected={selected}
        hasPrinted={hasPrinted}
        onTouchStart={(e) => handleClick(e, id, tableName)}
        showDropAnimation={showDropAnimation}
        grid={grid ?? false}
        shape={grid ? undefined : tableShape ?? 0}
        activity={activityStatus}
        staffCanMutate={staffCanMutate}
        hasBooking={tableHasBookings}>
        <TableNameWrapper>
          <span>{tableName}</span>
          {active && seatingTime && <SeatingTime>{seatingTime}</SeatingTime>}
        </TableNameWrapper>
        {showPeopleCount && <PeopleCount>{peopleCount}</PeopleCount>}
        {urgencyColor && <UrgencyDot color={urgencyColor} />}
        {noteText && <Note>{noteText}</Note>}
        {courseStage && <CourseStage color={courseStage.color}>{courseStage.label}</CourseStage>}
        {order && KDSShowOrderStatusInTSR && (
          <OrdersCount color={statusBackgroundColor(order.status)}>
            {i18n(order.status)}
          </OrdersCount>
        )}
      </Wrapper>

      {showModal && (
        <SelectOptionModal
          enableCustomInput
          title={i18n('TM.Coverage')}
          options={range(1, 8)}
          onSelect={(count) => handleSelectCoverage(count as number)}
        />
      )}
    </>
  );
};

export default React.memo(TableItem);
